/**
 * Ljuddämpare: the purchase requires proof of an EXISTING licence.
 *
 * Since 1 July 2022 a suppressor needs no licence of its own, but a customer may
 * only buy one if they already hold a weapon it fits. Torsbo therefore needs a
 * copy of the customer's ID and weapon licence before the order can be picked —
 * and today nothing on the storefront says so, which means staff chase every
 * such order by hand.
 *
 * DELIBERATELY NOT `th_licenspliktig`. That attribute means "may only be handed
 * over against a licence", which is a different claim and drags different copy
 * with it: the weapon delivery paragraph, the cart's mixed-order notice and the
 * order confirmation's "Vapnet reserveras i 14 dagar i väntan på licens" are all
 * wrong for a suppressor, and marking one licenspliktig would state, falsely,
 * that it is. The two conditions are orthogonal and stay orthogonal.
 *
 * Nor is it a `SECTIONS` entry: a section is exclusive, so claiming a suppressor
 * for one would also take over its trust badges and delivery copy — a side
 * effect nobody asked for. See lib/product-sections.ts.
 */

/**
 * Magento category ID for Ljuddämpare (`vapen/ljuddampare`).
 *
 * The ID rather than the url path on purpose. A path rule survives the store's
 * URL suffix being added or dropped — `underSlug` in product-sections.ts has a
 * case for exactly that — but it breaks silently the day someone edits the
 * category's URL Key, and a rename is far likelier than a suffix change. The ID
 * survives both a rename and a move.
 */
export const LJUDDAMPARE_CATEGORY_ID = '68';

/**
 * Where the customer sends their ID and licence.
 *
 * Kept beside the rule rather than in the copy, so the notice, the checkout
 * checkbox and the order confirmation cannot drift to different addresses — and
 * so each can render it as a real mailto link rather than as plain text inside a
 * translated sentence.
 */
export const SUPPORT_EMAIL = 'kassa@torsbohandels.com';

/**
 * Does this product require the customer to prove an existing licence?
 *
 * Takes every category ID the product carries, because products sit in campaign
 * categories alongside their real ones and only one of them is the answer.
 *
 * Note this reads category MEMBERSHIP: a suppressor filed only outside
 * Ljuddämpare — sold as a bundle child, say — would not be caught. That is the
 * known cost of a category rule over a per-product attribute; if it ever bites,
 * the fix is a `th_` attribute read the way `requiresLicense` is.
 */
export function requiresLicenseProof(
  categoryIds: readonly (string | number)[],
): boolean {
  // Coerced because the type lies. `CategoryReference.id` is declared `string`,
  // but the adapter's `mapCategories` — the product's own categories — assigns
  // Magento's Int straight through without a String(), where `mapCategory` (a
  // category page) does stringify. So the product page hands us numbers and the
  // extras query hands us strings, for the same field on the same product.
  // Comparing without coercion silently failed on the product page while the
  // cart matched, which is a notice that vanishes exactly where it is needed
  // first.
  return categoryIds.some((id) => String(id) === LJUDDAMPARE_CATEGORY_ID);
}
